import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowUp, ArrowDown, Minus, Instagram, Facebook, Megaphone, Star, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
export const fmtMonth = (m) => { if (!m) return ''; const [y, mo] = m.split('-'); return `${MONTHS_ES[parseInt(mo, 10) - 1]} ${y}`; };
export const nf = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 });
export const eur = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;

export function Delta({ v, inverse }) {
  if (v === null || v === undefined) return <span className="text-xs text-muted-foreground inline-flex items-center"><Minus className="w-3 h-3" /></span>;
  const up = v >= 0;
  const good = inverse ? !up : up;
  return <span className={`text-xs inline-flex items-center font-medium ${good ? 'text-emerald-600' : 'text-red-600'}`}>{up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(v)}%</span>;
}

const fmtVal = (value, isEur, suffix) => ((value === null || value === undefined) ? '—' : (isEur ? eur(value) : `${nf(value)}${suffix || ''}`));

export function Metric({ label, value, delta, isEur, suffix, inverse, prev, tone }) {
  return (
    <div className={`rounded-lg border p-3 print-avoid ${tone || ''}`} data-testid={`report-metric-${label}`}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <div className="flex items-end justify-between gap-1">
        <p className={`${tone ? 'text-2xl' : 'text-xl'} font-semibold tabular-nums`}>{fmtVal(value, isEur, suffix)}</p>
        <Delta v={delta} inverse={inverse} />
      </div>
      {prev !== undefined && <p className="text-[10px] text-muted-foreground mt-0.5">Mes anterior: {fmtVal(prev, isEur, suffix)}</p>}
    </div>
  );
}

export const toHtml = (a) => {
  if (!a) return '';
  if (/^\s*</.test(a)) return a;
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return a.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
};

export function ReportPrintHeader({ title, month }) {
  return (
    <div className="hidden print:flex items-end justify-between rounded-xl px-6 py-5 mb-2 text-white" style={{ background: '#085041' }}>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] opacity-80">Karma Digital</p>
        <p className="text-2xl font-bold mt-1">{title}</p>
      </div>
      <p className="text-lg font-semibold capitalize">{fmtMonth(month)}</p>
    </div>
  );
}

function Trend({ title, data, dataKey, color }) {
  const has = (data || []).some((d) => d[dataKey] !== null && d[dataKey] !== undefined);
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs font-medium mb-2">{title}</p>
      {!has ? <p className="text-xs text-muted-foreground italic py-8 text-center">Sin datos</p> : (
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={data} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip labelFormatter={fmtMonth} formatter={(v) => nf(v)} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function TopPosts({ igPosts, fbPosts }) {
  const all = [
    ...(igPosts || []).map((p) => ({ ...p, red: 'Instagram' })),
    ...(fbPosts || []).map((p) => ({ ...p, red: 'Facebook' })),
  ].filter((p) => p && (p.text || p.content || p.picture || p.image));
  if (all.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Star className="w-4 h-4 text-amber-500" /> Publicaciones destacadas</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {all.slice(0, 6).map((p, i) => {
          const img = p.picture || p.image;
          const link = p.url || p.link || p.permalink;
          const txt = (p.text || p.content || '').slice(0, 110);
          return (
            <div key={i} className="rounded-lg border overflow-hidden" data-testid={`report-top-post-${i}`}>
              {img && <img src={img} alt="" className="w-full h-32 object-cover" />}
              <div className="p-2.5 space-y-1">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground">{p.red}</p>
                {txt && <p className="text-xs line-clamp-2">{txt}</p>}
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{p.engagement != null ? `${nf(p.engagement)} engagement` : (p.interactions != null ? `${nf(p.interactions)} interacciones` : '')}</span>
                  {link && <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary print:hidden">Ver <ExternalLink className="w-3 h-3" /></a>}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function ContentReportView({ data }) {
  const cur = data?.current || {}; const ig = cur.instagram || {}; const fb = cur.facebook || {};
  const cmp = data?.comparatives || {}; const trends = data?.trends || [];
  return (
    <div className="space-y-5" data-testid="content-report-view">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Instagram className="w-4 h-4 text-pink-600" /> Instagram (orgánico)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Metric label="Seguidores" value={ig.followers} delta={cmp.instagram?.followers} />
          <Metric label="Nuevos seguidores" value={ig.followers_gained} delta={cmp.instagram?.followers_gained} />
          <Metric label="Alcance" value={ig.reach} delta={cmp.instagram?.reach} />
          <Metric label="Impresiones" value={ig.impressions} delta={cmp.instagram?.impressions} />
          <Metric label="Interacciones" value={ig.engagement} delta={cmp.instagram?.engagement} />
          <Metric label="Visitas al perfil" value={ig.profile_views} delta={cmp.instagram?.profile_views} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Facebook className="w-4 h-4 text-blue-600" /> Facebook (orgánico)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Seguidores" value={fb.followers} delta={cmp.facebook?.followers} />
          <Metric label="Visitas a la página" value={fb.page_views} delta={cmp.facebook?.page_views} />
          <Metric label="Impresiones" value={fb.impressions} delta={cmp.facebook?.impressions} />
          <Metric label="Interacciones" value={fb.interactions} delta={cmp.facebook?.interactions} />
        </CardContent>
      </Card>
      <TopPosts igPosts={ig.top_posts} fbPosts={fb.top_posts} />
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Evolución (últimos meses)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Trend title="Seguidores IG" data={trends} dataKey="ig_followers" color="#db2777" />
          <Trend title="Alcance IG" data={trends} dataKey="ig_reach" color="#7c3aed" />
          <Trend title="Interacciones IG" data={trends} dataKey="ig_engagement" color="#0ea5e9" />
          <Trend title="Seguidores FB" data={trends} dataKey="fb_followers" color="#2563eb" />
        </CardContent>
      </Card>
    </div>
  );
}

export function AdsReportView({ data }) {
  const s = data?.current?.summary || {};
  const cmp = data?.comparatives?.summary || {};
  const campaigns = data?.current?.campaigns || [];
  const p = data?.previous?.summary;
  const pv = (k) => (data?.previous ? (p ? p[k] : null) : undefined);
  const ESTADO = {
    activa: { label: 'Activa', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    pausada: { label: 'Pausada', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    finalizada: { label: 'Finalizada', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  };
  const fmtDate = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  return (
    <div className="space-y-5" data-testid="ads-report-view">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Megaphone className="w-4 h-4 text-primary" /> Resultados totales (Meta Ads)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Inversión" value={s.spend} delta={cmp.spend} prev={pv('spend')} isEur tone="bg-emerald-50 border-emerald-200" />
            <Metric label="Leads / resultados" value={s.leads} delta={cmp.leads} prev={pv('leads')} tone="bg-sky-50 border-sky-200" />
            <Metric label="Coste por lead" value={s.cpl} delta={cmp.cpl} prev={pv('cpl')} isEur inverse tone="bg-amber-50 border-amber-200" />
            <Metric label="CTR" value={s.ctr} delta={cmp.ctr} prev={pv('ctr')} suffix="%" tone="bg-violet-50 border-violet-200" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Impresiones" value={s.impressions} delta={cmp.impressions} prev={pv('impressions')} />
            <Metric label="Clics" value={s.clicks} delta={cmp.clicks} prev={pv('clicks')} />
            <Metric label="Alcance" value={s.reach} delta={cmp.reach} prev={pv('reach')} />
            <Metric label="ROAS" value={s.roas} delta={cmp.roas} prev={pv('roas')} />
          </div>
          <p className="text-[11px] text-muted-foreground">Las flechas comparan con el mes anterior{data?.previous?.month ? ` (${fmtMonth(data.previous.month)})` : ''}. En coste por lead, bajar es bueno.</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Por campaña</CardTitle></CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center" data-testid="ads-report-no-campaigns">No hubo campañas con actividad este mes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ads-report-campaigns-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-muted-foreground border-b bg-muted/40">
                    <th className="py-2 pr-3">Campaña</th>
                    <th className="py-2 px-2 text-right">Inversión</th>
                    <th className="py-2 px-2 text-right">Impresiones</th>
                    <th className="py-2 px-2 text-right">Clics</th>
                    <th className="py-2 px-2 text-right">CTR</th>
                    <th className="py-2 px-2 text-right">Leads</th>
                    <th className="py-2 px-2 text-right">CPL</th>
                    <th className="py-2 pl-2 text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const est = c.estado ? ESTADO[c.estado] : null;
                    return (
                    <tr key={c.id} className="border-b last:border-0 print-avoid">
                      <td className="py-2 pr-3 max-w-[280px]">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate" title={c.name}>{c.name}</span>
                          {est && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wide shrink-0 ${est.cls}`} data-testid={`campaign-estado-${c.id}`}>
                              {est.label}
                            </span>
                          )}
                        </div>
                        {c.fecha_inicio && (
                          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`campaign-fechas-${c.id}`}>
                            Desde {fmtDate(c.fecha_inicio)}{c.fecha_fin ? ` · hasta ${fmtDate(c.fecha_fin)}` : ' · en curso'}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{eur(c.spend)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{nf(c.impressions)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{nf(c.clicks)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.ctr != null ? `${nf(c.ctr)}%` : '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{nf(c.leads)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{eur(c.cpl)}</td>
                      <td className="py-2 pl-2 text-right tabular-nums">{nf(c.roas)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PrintStyles() {
  return (
    <style>{`@media print {
      body * { visibility: hidden !important; }
      #printable-report, #printable-report * { visibility: visible !important; }
      #printable-report { position: absolute !important; left: 0; top: 0; width: 100%; padding: 0; }
      #printable-report * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      #printable-report [class*="shadow"] { box-shadow: none !important; }
      #printable-report .print-avoid, #printable-report tr { break-inside: avoid; page-break-inside: avoid; }
      #printable-report .overflow-x-auto { overflow: visible !important; }
      #printable-report table { font-size: 11px; }
      .recharts-wrapper, .recharts-surface { overflow: visible !important; }
      @page { margin: 12mm; }
    }`}</style>
  );
}
